import React from 'react';
import { useConfig } from '@cp/shared/config';
import { useCurrentUser } from '@cp/entities/currentUser';
import { UserType } from '@cp/entities/user';
import { currentClientNotificationApi } from '@cp/entities/client';
import { WebpushSubscriptionKeys } from '@common/model/client/notification/webpush/subscribe.request';
import { useEventCallback } from '@cp/ds/src/hooks';
import { INotificationDataType } from '@common/model/notification';
import { useDispatch } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import {
  IPushNotificationsContext,
  IPushNotificationsEventListener,
  PUSH_NOTIFICATIONS_DEFAULT_STATUS,
  PushNotificationManager,
  PushNotificationsContext,
  PushNotificationsEvent,
  PushNotificationsStatus,
} from 'src/shared/pushNotifications';
import { useFaviconManager } from 'src/shared/favico';

export const PushNotificationsProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const config = useConfig();
  const currentUser = useCurrentUser();
  const dispatch: ThunkDispatch<any, any, any> = useDispatch();
  const favico = useFaviconManager();
  const [status, setStatus] = React.useState<PushNotificationsStatus>(PUSH_NOTIFICATIONS_DEFAULT_STATUS);

  const onSubscriptionExists = useEventCallback(async (subscription: PushSubscriptionJSON) => {
    if (currentUser.type === UserType.Client && currentUser.id) {
      await dispatch(
        currentClientNotificationApi.endpoints.subscribeToWebpushNotifications.initiate({
          endpoint: String(subscription.endpoint),
          expiredAt: subscription.expirationTime ? new Date(subscription.expirationTime).toISOString() : undefined,
          keys: subscription.keys as WebpushSubscriptionKeys | undefined,
        }),
      );
    } else {
      // Subscription exists but there are no authenticated client -> do nothing in that case
    }
  });

  const managerRef = React.useRef(
    new PushNotificationManager({
      onSubscriptionExists,
      onStatusUpdated: setStatus,
      applicationServerKey: config.get('WEB_PUSH_NOTIFICATIONS_PUBLIC_KEY'),
    }),
  );

  React.useEffect(() => {
    managerRef.current.checkSubscription();
  }, [currentUser.type, currentUser.id]);

  React.useEffect(() => {
    const listener: IPushNotificationsEventListener = (message) => {
      if (
        message.data.event === PushNotificationsEvent.NotificationReceived &&
        message.data.notification.data?.type === INotificationDataType.UpdateUnreadCount
      ) {
        const { unreadCount } = message.data.notification.data;
        favico.badge(unreadCount);
      }
    };
    const manager = managerRef.current;

    manager.addEventListener(listener);
    return () => {
      manager.removeEventListener(listener);
    };
  }, [favico]);

  const value = React.useMemo<IPushNotificationsContext>(
    () => ({
      manager: managerRef.current,
      status,
    }),
    [status],
  );

  return <PushNotificationsContext.Provider value={value}>{children}</PushNotificationsContext.Provider>;
};
